import { Component, Input, Output, OnChanges, SimpleChanges, SimpleChange, EventEmitter, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { ComponentBase, Changeable } from 'common';
import {
	UserDto, UserClass, DirectoryUserDto, AccountType, GetDirectoryUsersResultDto,
	getAccountTypeLabel,
	GroupDto,
	GetGroupsContainingUserResultDto,
	GetGroupsContainingDirectoryUserResultDto
} from 'UvExplorerServer.Dtos.TS/Auth';
import { UsersService } from 'identity';
import { MessageService } from 'base';
import { HashSet, List, ObjUtils } from '../../../DiscoveryEngine.TS/Utils';


@Component({
	selector: 'directory-user',
	templateUrl: './directory-user.component.html',
	styleUrls: ['./directory-user.component.scss']
})
export class DirectoryUserComponent extends ComponentBase implements OnInit, OnChanges, Changeable {

	@Input()
	public userType: AccountType;

	@Input()
	public domains: string[];

	@Input()
	public target: UserDto;

	@Input()
	public create: boolean;

	@Input()
	public dirUsers: DirectoryUserDto[];

	@Input()
	public allowSearch: boolean;

	public searchButtonLabel: string;
	public searchEmptyResultMessage: string;
	public syncUserButtonLabel: string;

	public searchValue: string;
	public domain: string;
	public dirUserSelection: DirectoryUserDto;
	private dirUserDirectoryId: string;
	private dirUserDirectoryId_2: string;
	private dirUserDomainName: string;

	public form: UntypedFormGroup;

	public username: UntypedFormControl;
	public firstName: UntypedFormControl;
	public lastName: UntypedFormControl;
	public email: UntypedFormControl;
	public userClass: UntypedFormControl;
	public active: UntypedFormControl;

	public entries: any[];
	public excludedGroupGuids: string[];
	public groupSelectorVisible: boolean;

	private userGroups: HashSet<GroupDto>;
	private originalUserGroups: HashSet<GroupDto>;

	constructor(private fb: UntypedFormBuilder,
		private usersService: UsersService,
		private messageService: MessageService) {

		super();

		this.userType = AccountType.ActiveDirectory;
		this.domains = [];
		this.create = false;
		this.dirUsers = [];
		this.allowSearch = false;

		this.searchButtonLabel = "";
		this.searchEmptyResultMessage = "";
		this.syncUserButtonLabel = "";
		this.searchValue = "";
		this.domain = "";
		this.dirUserSelection = undefined;
		this.dirUserDirectoryId = "";
		this.dirUserDirectoryId_2 = "";
		this.dirUserDomainName = "";

		this.entries = [];
		this.excludedGroupGuids = [];
		this.groupSelectorVisible = false;

		this.userGroups = new HashSet<GroupDto>();
		this.originalUserGroups = new HashSet<GroupDto>();

		this.buildForm();
	}

	public ngOnInit(): void {
		this.initUserGroups();
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (changes["domains"]) {
			if (this.domains.length > 0) {
				this.domain = this.domains[0];
			}
			else {
				this.domain = "";
			}
		}

		if (changes["userType"]) {
			let userTypeString = getAccountTypeLabel(this.userType);

			this.searchButtonLabel = `Search ${userTypeString} Users`;
			this.searchEmptyResultMessage = `No matching users`;
			this.syncUserButtonLabel = `Sync User with ${userTypeString}`;
		}

		this.buildForm();
		this.searchValue = "";
		this.dirUserSelection = undefined;
		this.excludedGroupGuids = [];
		this.groupSelectorVisible = false;
		this.initUserGroups();
	}

	private initUserGroups(): void {
		this.userGroups.Clear();
		this.originalUserGroups.Clear();
		this.entries = [];

		let _init = (groups: List<GroupDto>) => {
			groups.ForEach((group: GroupDto) => {
				this.userGroups.Add(group);
				this.originalUserGroups.Add(group);
			});

			this.rebuildEntries();
		}

		if (this.create) {
			if (!!this.dirUserSelection) {
				this.usersService.getGroupsContainingDirectoryUser(this.userType, this.dirUserSelection.PermanentId,
					this.dirUserSelection.EphemeralId).subscribe(
						(result: GetGroupsContainingDirectoryUserResultDto) => {
							_init(result.Groups);
						},
						(error) => {
							this.messageService.displayErrorMessage("Could not retrieve user's groups");
						});
			}
		}
		else {
			if (!!this.target) {
				this.usersService.getGroupsContainingUser(this.target.Guid).subscribe(
					(result: GetGroupsContainingUserResultDto) => {
						_init(result.Groups);
					},
					(error) => {
						this.messageService.displayErrorMessage("Could not retrieve user's groups");
					});
			}
		}
	}

	public get hasDirUsers(): boolean {
		return (!!this.dirUsers && this.dirUsers.length > 0);
	}

	public get hasGroupEntries(): boolean {
		return (!!this.entries && this.entries.length > 0);
	}

	public get enableSearch(): boolean {
		return (this.isSearchValueValid(this.searchValue) && !!this.domain);
	}

	private isSearchValueValid(searchValue: string): boolean {
		if (!searchValue) {
			return false;
		}
		return !!searchValue.match(/^[a-zA-Z0-9\-\_\.\* ]+$/i);
	}

	private buildForm(): void {
		let username = "";
		let firstName = "";
		let lastName = "";
		let email = "";
		let userClass = "Regular";
		let active = false;

		this.dirUserDirectoryId = "";
		this.dirUserDirectoryId_2 = "";
		this.dirUserDomainName = "";

		if (this.target) {
			username = this.target.Username;
			firstName = this.target.FirstName;
			lastName = this.target.LastName;
			email = this.target.Email;
			userClass = UserClass[this.target.UserClass];
			active = this.target.IsActive;

			this.dirUserDirectoryId = this.target.DirectoryId;
			this.dirUserDirectoryId_2 = this.target.DirectoryId_2;
			this.dirUserDomainName = this.target.DirectoryDomainName;
		}

		this.username = new UntypedFormControl(username, Validators.required);
		this.firstName = new UntypedFormControl(firstName, Validators.required);
		this.lastName = new UntypedFormControl(lastName, Validators.required);
		this.email = new UntypedFormControl(email, Validators.required);
		this.userClass = new UntypedFormControl(userClass);
		this.active = new UntypedFormControl(active);

		this.form = this.fb.group({
			username: this.username,
			firstName: this.firstName,
			lastName: this.lastName,
			email: this.email,
			userClass: this.userClass,
			active: this.active,
		});
	}

	public get enableApply(): boolean {
		return (this.tempChanges && this.tempChangesValid);
	}

	public get tempChanges(): boolean {
		return this.form.dirty;
	}

	public get tempChangesValid(): boolean {
		return this.form.valid;
	}

	public get invalidTempChanges(): string[] {
		let result: string[] = [];
		if (!this.form.valid) {
			result.push("Directory User");
		}
		return result;
	}

	public applyTempChanges(): void {
		if (this.target) {
			this.getChanges(this.target);
			this.buildForm();
		}
	}

	public discardTempChanges(): void {
		this.buildForm();
	}

	private getChanges(user: UserDto): void {
		user.Username = this.username.value;
		user.FirstName = this.firstName.value;
		user.LastName = this.lastName.value;
		user.Email = this.email.value;
		user.UserClass = UserClass[<string>this.userClass.value];
		user.IsActive = this.active.value;
		user.DirectoryId = this.dirUserDirectoryId;
		user.DirectoryId_2 = this.dirUserDirectoryId_2;
		user.DirectoryDomainName = this.dirUserDomainName;
	}

	public searchForUsers(): void {
		this.messageService.startWork();
		this.usersService.getDirectoryUsers(this.userType, this.domain, this.searchValue, "", "").subscribe(
			(result: GetDirectoryUsersResultDto) => {
				this.dirUsers = result.Users.Users.ToList().ToArray();
				this.messageService.endWork();
			},
			(error) => {
				this.messageService.displayErrorMessage("User search failed");
				this.messageService.endWork();
			});
	}

	public onRowSelect(): void {
		if (this.dirUserSelection) {
			let [name, firstName, lastName] = this.parseDirUserNames(this.dirUserSelection);

			this.username.setValue(this.dirUserSelection.Username);
			this.firstName.setValue(firstName);
			this.lastName.setValue(lastName);
			this.email.setValue(this.dirUserSelection.Email);
			this.dirUserDirectoryId = this.dirUserSelection.PermanentId;
			this.dirUserDirectoryId_2 = this.dirUserSelection.EphemeralId;
			this.dirUserDomainName = this.domain;

			this.initUserGroups();

			this.form.markAsDirty();
		}
	}

	private parseDirUserNames(dirUser: DirectoryUserDto): string[] {
		let name = dirUser.Name;
		let firstName = dirUser.FirstName;
		let lastName = dirUser.LastName;

		if (dirUser.HasName && !dirUser.HasFirstName && !dirUser.HasLastName) {
			let nameParts = dirUser.Name.trim().split(/\s+/);
			if (!!nameParts && nameParts.length > 0) {
				switch (nameParts.length) {
					case 1:
						firstName = nameParts[0];
						break;
					case 2:
						firstName = nameParts[0];
						lastName = nameParts[1];
						break;
					default:
						firstName = nameParts[0];
						nameParts.splice(0, 1);
						lastName = nameParts.join(' ');
						break;
				}
			}
		}

		return [name, firstName, lastName];
	}

	public onRowUnselect(): void {
		this.username.setValue("");
		this.firstName.setValue("");
		this.lastName.setValue("");
		this.email.setValue("");
		this.dirUserDirectoryId = "";
		this.dirUserDirectoryId_2 = "";
		this.dirUserDomainName = "";

		this.userGroups.Clear();
		this.originalUserGroups.Clear();
		this.rebuildEntries();
	}

	public syncUser(): void {
		this.messageService.startWork();
		this.usersService.getDirectoryUsers(this.target.AccountType, this.target.DirectoryDomainName,
			"", this.target.DirectoryId, this.target.DirectoryId_2).subscribe(
				(result: GetDirectoryUsersResultDto) => {
					if (result.HasUsers && result.Users.Users.Count == 1) {
						let dirUser: DirectoryUserDto = result.Users.Users.First();
						let [name, firstName, lastName] = this.parseDirUserNames(dirUser);

						if (dirUser.HasUsername) {
							this.username.setValue(dirUser.Username);
						}
						if (!!firstName) {
							this.firstName.setValue(firstName);
						}
						if (!!lastName) {
							this.lastName.setValue(lastName);
						}
						if (dirUser.HasEmail) {
							this.email.setValue(dirUser.Email);
						}

						this.form.markAsDirty();
						this.messageService.displayInfoMessage("User sync succeeded");
					}
					else {
						this.messageService.displayErrorMessage("User sync failed");
					}
					this.messageService.endWork();
				},
				(error) => {
					this.messageService.displayErrorMessage("User sync failed");
					this.messageService.endWork();
				});
	}

	private rebuildEntries(): void {
		this.entries = [];

		this.userGroups.ForEach((group: GroupDto) => {
			this.addEntry(group);
		});
	}

	private addEntry(group: GroupDto): void {
		if (!this.entries.some((entry) => entry.tag.Guid === group.Guid)) {
			let entry = {
				groupname: group.Name,
				class: UserClass[group.UserClass],
				type: getAccountTypeLabel(group.AccountType),
				active: (group.IsActive ? 'Yes' : 'No'),
				tag: group,
				isNative: (group.AccountType === AccountType.Native),
			};

			this.entries.push(entry);
		}
	}

	public addGroups(): void {
		this.rebuildExcludedGroupGuids();
		this.groupSelectorVisible = true;
	}

	private rebuildExcludedGroupGuids(): void {
		let result = this.groupsToGuids(this.userGroups);

		this.usersService.groupTree.Groups.ForEach((group: GroupDto) => {
			if (!group.HasName || group.AccountType !== AccountType.Native) {
				result.Add(group.Guid);
			}
		});

		this.excludedGroupGuids = [];
		result.ForEach((guid: string) => {
			this.excludedGroupGuids.push(guid);
		});
	}

	public addUserToSelectedGroups(selectedGroups: GroupDto[]): void {
		if (!!selectedGroups && selectedGroups.length > 0) {
			selectedGroups.forEach((group: GroupDto) => {
				this.userGroups.Add(group);
			});
			this.rebuildEntries();
			this.form.markAsDirty();
		}
	}

	public removeGroup(group: GroupDto): void {
		this.userGroups.Remove(group);
		this.rebuildEntries();
		this.form.markAsDirty();
	}

	public get addedUserGroups(): HashSet<string> {
		let userGroupsGuids = this.groupsToGuids(this.userGroups);
		let originalUserGroupsGuids = this.groupsToGuids(this.originalUserGroups);
		return ObjUtils.SetDifference(userGroupsGuids, originalUserGroupsGuids);
	}

	public get removedUserGroups(): HashSet<string> {
		let userGroupsGuids = this.groupsToGuids(this.userGroups);
		let originalUserGroupsGuids = this.groupsToGuids(this.originalUserGroups);
		return ObjUtils.SetDifference(originalUserGroupsGuids, userGroupsGuids);
	}

	private groupsToGuids(groups: HashSet<GroupDto>): HashSet<string> {
		let result = new HashSet<string>();
		groups.ForEach((group: GroupDto) => result.Add(group.Guid));
		return result;
	}

}
